<template>
    <b-modal
        id="registration-modal"
        ref="registration-modal"
        class="modal-content"
        no-close-on-backdrop
        no-close-on-esc
        hide-header
        hide-footer
        centered
    >
        <div
            class="modal-container"
            :style="{ color: theming.registrationModalTextColor, backgroundColor: theming.registrationModalBgColor }"
        >
            <b-button class="close-button" @click="hideModal">
                <times-icon :size="26" :color="theming.registrationModalTextColor" />
            </b-button>
            <div class="modal-header">
                <div class="body-head">
                    {{ $t('registrationModal.instructions') }}
                </div>
                <div class="body-text">
                    {{ $t('registrationModal.description') }}
                </div>
            </div>

            <div class="form-inputs-container">
                <div
                    class="form-group"
                >
                    <label>
                        {{ $t('settingsRoute.username') }} *
                    </label>
                    <input
                        v-model="form.username"
                        type="text"
                        class="form-control"
                        name="username"
                        :class="{
                            'is-valid': form.username,
                            'is-invalid': form.username === '' && errorMessage
                        }"
                        trim
                    >
                </div>
                <div class="passwords-container">
                    <div
                        class="form-group"
                    >
                        <label>
                            {{ $t('settingsRoute.newPassword') }} *
                        </label>
                        <input
                            v-model="form.newPassword"
                            type="password"
                            class="form-control"
                            name="New Password"
                            :class="{
                                'is-valid': form.newPassword,
                                'is-invalid': form.newPassword === '' && errorMessage
                            }"
                            trim
                        >
                    </div>
                    <div
                        class="form-group"
                    >
                        <label>
                            {{ $t('settingsRoute.confirmPassword') }} *
                        </label>
                        <input
                            v-model="form.confirmPassword"
                            type="password"
                            class="form-control"
                            name="Confirm Password"
                            :class="{
                                'is-valid': form.confirmPassword,
                                'is-invalid': form.confirmPassword !== form.newPassword
                            }"
                            trim
                        >
                    </div>
                </div>
            </div>
            <div class="buttons-container">
                <div
                    :class="{ disabled: emptyFieldExists }"
                    :style="{
                        backgroundColor: theming.registrationModalButtonBgColor,
                        color: theming.registrationModalButtonTextColor
                    }"
                    class="custom-btn save-btn"
                    @click="submit"
                >
                    {{ $t('registrationModal.save') }}
                </div>
                <div
                    class="custom-btn cancel-btn"
                    @click="hideModal"
                >
                    {{ $t('registrationModal.skip') }}
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
    import playerService from '@/services/player-service'
    import badWordsUtil from '@/utils/bad-words'
    import validationUtils from '@/utils/validation-utils'
    import TimesIcon from '@/components/icons/TimesIcon.vue'
    import emitter from '@/utils/emitter'

    export default {
        components: { TimesIcon },
        data() {
            return {
                errorMap: {
                    PLAYER_USERNAME_EXISTS: this.$t('settingsRoute.usernameExists'),
                    INVALID_DATA: this.$t('settingsRoute.invalidData')
                },
                errorMessage: null,
                form: {
                    username: '',
                    newPassword: '',
                    confirmPassword: ''
                },
                isFormValid: true
            }
        },
        computed: {
            theming() {
                return this.$store.getters.getTheming
            },
            emptyFieldExists() {
                return Object.values(this.form).some(value => !value)
            },
            registrationPopupEnabled() {
                return this.$store.getters.getConfigFeatures?.registrationPopupEnabled ?? false
            },
            showRegistrationModal() {
                return this.$store.getters.getShowRegistrationModal
            },
            isFirstLoginSession() {
                return this.$store.getters.getCurrentUser?.firstLoginSession
            },
            shouldShowRegistrationModal() {
                return this.registrationPopupEnabled && this.showRegistrationModal && this.isFirstLoginSession
            }
        },
        watch: {
            shouldShowRegistrationModal(newValue) {
                if (newValue) {
                    this.$store.commit('setShowRegistrationModal', false)
                    emitter.emit('openRegistrationModal')
                }
            }
        },
        created() {
            emitter.on('openRegistrationModal', () => {
                this.$bvModal.show('registration-modal')
            })
        },
        methods: {
            validateForm() {
                this.isFormValid = true
                const formErrors = {
                    username: `${this.$t('settingsRoute.username')} ${this.$t('settingsRoute.isRequired')}`,
                    newPassword: `${this.$t('settingsRoute.newPassword')} ${this.$t('settingsRoute.isRequired')}`,
                    confirmPassword: `${this.$t('settingsRoute.confirmPassword')} ${this.$t('settingsRoute.isRequired')}`
                }
                for (const [key, value] of Object.entries(this.form)) {
                    if (!value) {
                        this.isFormValid = false
                        this.errorMessage = formErrors[key]
                        setTimeout(() => {
                            this.errorMessage = null
                        }, 5000)
                        break
                    }
                }

                if (this.form?.username?.length < 6 || this.form?.username?.length > 24) {
                    this.isGeneralFormValid = false
                    this.errorMessage = `${this.$t('settingsRoute.username')} ${this.$t('settingsRoute.mustBeBetween6And24Characters')}`
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                    return
                }
                if (this.form?.username.includes(' ')) {
                    this.isGeneralFormValid = false
                    this.errorMessage = `${this.$t('settingsRoute.username')} ${this.$t('settingsRoute.mustNotContainSpaces')}`
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                    return
                }
                if (badWordsUtil.hasBadWords(this.form?.username)) {
                    this.isGeneralFormValid = false
                    this.errorMessage = `${this.$t('settingsRoute.username')} ${this.$t('settingsRoute.mustNotContainNaughtyWords')}`
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                    return
                }

                if (!validationUtils.userNameValidation(this.form?.username)) {
                    this.isGeneralFormValid = false
                    this.errorMessage = `${this.$t('settingsRoute.username')} ${this.$t('settingsRoute.mustBeOnlyLetters')}`
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                    return
                }
                if (this.form?.newPassword?.length < 6) {
                    this.isGeneralFormValid = false
                    this.errorMessage = `${this.$t('settingsRoute.newPassword')} ${this.$t('settingsRoute.needsAtLeast6Characters')}`
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                    return
                }
                if (this.form?.newPassword !== this.form?.confirmPassword) {
                    this.isGeneralFormValid = false
                    this.errorMessage = `${this.$t('settingsRoute.confirmPassword')} ${this.$t('settingsRoute.notMatch')} ${this.$t('settingsRoute.newPassword')}`
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                    return
                }
                if (this.form?.newPassword?.includes(' ') || this.form?.confirmPassword?.includes(' ')) {
                    this.isGeneralFormValid = false
                    this.errorMessage = `${this.$t('settingsRoute.newPassword')} ${this.$t('settingsRoute.mustNotContainSpaces')}`
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                }
            },
            resetForm() {
                this.form.username = ''
                this.form.newPassword = ''
                this.form.confirmPassword = ''
            },
            hideModal() {
                this.resetForm()
                this.$refs['registration-modal'].hide()
            },
            async submit() {
                this.validateForm()
                if (!this.isFormValid) {
                    return
                }
                try {
                    const response = await playerService.updateCredentials({
                        username: this.form.username,
                        password: this.form.newPassword
                    })
                    this.resetForm()
                    this.$store.commit('login', response.data.token)
                    this.$store.dispatch('refreshCurrentUser')
                    this.hideModal()
                } catch (error) {
                    this.errorMessage = this.errorMap[error.response.data.code]
                    setTimeout(() => {
                        this.errorMessage = null
                    }, 5000)
                }
            }
        }
    }
</script>

<style>
#registration-modal.modal {
    backdrop-filter: blur(2px);
}
</style>

<style lang="scss" scoped>
.modal-container {
    position: relative;
    width: 90%;
    margin: 0 auto;
    min-height: 300px;
    padding: 2.5rem 1.375rem 1.375rem;
    border-radius: 25px;
    justify-content: space-evenly;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1.5rem;
    border: 0;
}

.avatar-container {
    position: absolute;
    width: 200px;
    top: 100%;
    left: 46%;
    transform: translate(-50%, -50%);
}

.avatar-container img {
    width: 100%;
    height: auto;
}

.close-button {
    position: absolute;
    background: none;
    z-index: 3;
    right: 1.375rem;
    top: 1.375rem;
    outline: none;
    border: none;
}

.close-button:hover {
    background: none;
    outline: none;
    border: none;
}

.body-head {
    font-weight: 700;
    font-size: 1.25rem;
    text-align: center;
    margin: 0 auto;
}

.body-text {
    font-weight: 400;
    font-size: 1rem;
    text-align: center;
    margin: 0 auto;
    padding: 0.5rem 1rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.buttons-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    margin: 2rem auto;
}

.custom-btn {
    width: 100%;
    max-width: 250px;
    min-width: 200px;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    filter: drop-shadow(0 0 0.2rem rgb(0 0 0 / 30%));
    border-radius: 10px;
    text-decoration: none;
    cursor: pointer;
}

.ok {
    background: #1b8fbd;
    font-weight: 600;
    font-size: 1.1rem;
    letter-spacing: 1px;
}

.form-control {
    max-width: 420px;
    width: 100%;
}

.form-group {
    width: 100%;
    max-width: 420px;
}

.form-inputs-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.passwords-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    width: 100%;
}

.save-btn {
    font-weight: 600;
    font-size: 1.5rem;
}

.cancel-btn {
    font-size: 1.25rem;
    text-decoration: underline;
    background: transparent;
}

@media (min-width: 1200px) {
    .modal-container {
        min-width: 1043px;
        min-height: 630px;
        padding-inline: 4rem;
    }

    .form-inputs-container {
        align-items: flex-start;
    }

    .passwords-container {
        justify-content: space-between;
    }

    .form-group {
        max-width: 45%;
    }
}

</style>

<style lang="scss">
.modal-content {
    background: transparent !important;
    border: none !important;
}

#registration-modal .modal-dialog {
    max-width: 664px !important;
    max-height: 792px !important;
}

@media (min-width: 1200px) {
    #registration-modal .modal-dialog {
        max-width: 1043px !important;
        max-height: 630px !important;
    }
}
</style>
